// src/components/LocationStatusBadge.tsx
//
// Compact indicator for the user's current location signal. Sits in
// the top-right of the app shell beside the notification bell.
//
// States (color × icon):
//   green + GPS-fixed icon       → precise (accuracy ≤ 50m)
//   yellow + crosshair icon      → coarse (accuracy ≤ 500m or unknown but present)
//   red + GPS-off icon           → none / denied / error
//   grey + spinner              → loading / first-fix
//
// Tapping the badge opens the Discover settings sheet (where the
// user can change distance preferences and inspect their location
// status). The sheet open/close is managed at the AppShell level
// so the badge doesn't need to know about Discover internals.

import React from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  GpsFixed,
  GpsNotFixed,
  GpsOff,
} from '@mui/icons-material';
import { useLocation } from '../hooks/useLocation';

export type LocationStatus = 'precise' | 'coarse' | 'none' | 'loading';

interface LocationStatusBadgeProps {
  onClick: () => void;
}

const PRECISE_M = 50;
const COARSE_M = 500;

export function deriveLocationStatus(
  loading: boolean,
  hasPermission: boolean,
  accuracyMeters: number | undefined,
  hasFix: boolean
): LocationStatus {
  if (loading && !hasFix) return 'loading';
  if (!hasPermission && !hasFix) return 'none';
  if (!hasFix) return 'none';
  // We have coords. Bucket by accuracy.
  if (accuracyMeters === undefined) return 'coarse';
  if (accuracyMeters <= PRECISE_M) return 'precise';
  if (accuracyMeters <= COARSE_M) return 'coarse';
  return 'coarse';
}

export const LocationStatusBadge: React.FC<LocationStatusBadgeProps> = ({
  onClick,
}) => {
  const { location, loading, hasPermission } = useLocation();
  const accuracy = location?.accuracy;
  const hasFix = !!(location?.latitude && location?.longitude);
  const status = deriveLocationStatus(loading, hasPermission, accuracy, hasFix);

  const dotColor =
    status === 'precise'
      ? '#22c55e' // green-500
      : status === 'coarse'
      ? '#f59e0b' // amber-500
      : status === 'loading'
      ? '#94a3b8' // slate-400
      : '#ef4444'; // red-500

  const Icon =
    status === 'precise' ? GpsFixed : status === 'coarse' ? GpsNotFixed : GpsOff;

  const tooltip =
    status === 'precise'
      ? `Location precise${accuracy ? ` (±${Math.round(accuracy)}m)` : ''}`
      : status === 'coarse'
      ? `Location approximate${accuracy ? ` (±${Math.round(accuracy)}m)` : ''}`
      : status === 'loading'
      ? 'Getting your location…'
      : 'Location unavailable — tap to fix';

  return (
    <Tooltip title={tooltip} placement="bottom">
      <IconButton
        onClick={onClick}
        size="small"
        aria-label={tooltip}
        sx={{
          position: 'relative',
          width: 36,
          height: 36,
          color: 'text.secondary',
        }}
      >
        {status === 'loading' ? (
          <CircularProgress size={18} sx={{ color: dotColor }} />
        ) : (
          <Icon fontSize="small" />
        )}
        {/* Status dot — bottom-right of the icon */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 6,
            right: 6,
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: dotColor,
            border: '1.5px solid',
            borderColor: 'background.paper',
            // Subtle pulse only when we don't yet have a fix
            animation:
              status === 'loading'
                ? 'locStatusPulse 1.4s ease-in-out infinite'
                : 'none',
            '@keyframes locStatusPulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.4 },
            },
          }}
        />
      </IconButton>
    </Tooltip>
  );
};
